// Drop-in for @jammable/ui-components/Accordion on SEO pages, built on
// <details>/<summary>.
//
// Radix Accordion renders a closed item's content as nothing at all
// (@radix-ui/react-collapsible: `children: isOpen && children`, even with
// forceMount), so every FAQ answer on the voice pages and the hubs was
// missing from the HTML Google fetches - while the same answers sat in
// FAQPage JSON-LD. A closed <details> keeps its content in the document; the
// browser only hides it. It also opens before hydration, with no JS.
//
// Same component names and props as the Radix version, so a page switches by
// changing its import. type="single" maps to a shared `name` attribute
// (exclusive accordion in current browsers; older ones just allow several
// open). `value` and `collapsible` are accepted and ignored.
import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@jammable/ui-core";

const GroupName = React.createContext<string | undefined>(undefined);

type RootProps = React.HTMLAttributes<HTMLDivElement> & {
  type?: "single" | "multiple";
  collapsible?: boolean;
};

export function Accordion({
  type,
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  collapsible: _collapsible,
  children,
  ...props
}: RootProps) {
  const id = React.useId();
  return (
    <div {...props}>
      <GroupName.Provider value={type === "single" ? `faq-${id}` : undefined}>
        {children}
      </GroupName.Provider>
    </div>
  );
}

type ItemProps = React.DetailsHTMLAttributes<HTMLDetailsElement> & {
  value?: string;
};

export function AccordionItem({
  className,
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  value: _value,
  ...props
}: ItemProps) {
  const name = React.useContext(GroupName);
  return (
    <details
      name={name}
      className={cn(className, "tw-group tw-p-0.5")}
      {...props}
    />
  );
}

export function AccordionTrigger({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <summary
      className={cn(
        "tw-flex tw-flex-1 tw-cursor-pointer tw-list-none tw-items-center tw-justify-between tw-bg-transparent tw-px-0 tw-py-4 tw-text-left tw-text-xl tw-font-semibold tw-tracking-tight tw-text-black hover:tw-underline [&::-webkit-details-marker]:tw-hidden",
        className,
      )}
    >
      {/* Radix wrapped the trigger in an <h3>; keep the heading outline but
          let the summary's classes set the type. */}
      <h3
        className="tw-m-0"
        style={{ font: "inherit", letterSpacing: "inherit", color: "inherit" }}
      >
        {children}
      </h3>
      <ChevronDown className="tw-h-4 tw-w-4 tw-shrink-0 tw-transition-transform tw-duration-200 group-open:tw-rotate-180" />
    </summary>
  );
}

export function AccordionContent({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="tw-overflow-hidden tw-px-0 tw-text-md tw-tracking-normal">
      <div className={cn("tw-pb-4 tw-pt-0 tw-tracking-normal", className)}>
        {children}
      </div>
    </div>
  );
}
